/**
 * The window a day chart draws: exactly one local calendar day of the place, midnight to midnight,
 * in the place's own time zone (never the device's). x is the number of real hours since that
 * day's midnight instant — 23 on the day clocks spring forward, 25 on the day they fall back (the
 * repeated hour is two distinct hours, never two samples at one x) — so clock-hour arithmetic
 * (`localHourFraction + 24 × days`) is wrong on exactly those days and is not used here.
 */
import type { HourPoint, WeatherReport } from '../../shared/contract'
import { fmtHour, formatter, localDate } from '../../shared/units'
import { HOUR, MINUTE, addDays, localHour, zoneOffset, zonedTime } from '../../engine/util'
import { hoursForDate } from './forecast'

export interface DayAxis {
  date: string
  timeZone: string
  /** Epoch ms of the day's first instant (its local midnight). */
  start: number
  /** Epoch ms of the next day's first instant. */
  end: number
  /** Real hours in the day: 24, 23 or 25 (23.5 / 24.5 for Lord Howe's half-hour shift). */
  length: number
  /** Fall-back days: the elapsed-hours span whose wall-clock times happen twice (e.g. 1 AM–2 AM EDT, then EST). */
  repeat: { from: number; to: number } | null
}

const axes = new Map<string, DayAxis>()

export function dayAxis(date: string, timeZone: string): DayAxis {
  const key = `${timeZone}|${date}`
  const hit = axes.get(key)
  if (hit) return hit
  const start = zonedTime(date, 0, timeZone)
  let end = zonedTime(addDays(date, 1), 0, timeZone)
  if (!(end > start)) end = start + 24 * HOUR
  const length = (end - start) / HOUR
  let repeat: DayAxis['repeat'] = null
  const shift = length - 24
  if (shift > 0) {
    // Clocks fell back at some instant inside the day: find it (the offset changes once).
    const before = zoneOffset(start, timeZone)
    let lo = start
    let hi = end
    while (hi - lo > MINUTE) {
      const mid = Math.max(lo + MINUTE, lo + Math.floor((hi - lo) / (2 * MINUTE)) * MINUTE)
      if (zoneOffset(mid, timeZone) === before) lo = mid
      else hi = mid
    }
    const at = (hi - start) / HOUR
    repeat = { from: at - shift, to: at + shift }
  }
  const axis = { date, timeZone, start, end, length, repeat }
  if (axes.size > 400) axes.clear()
  axes.set(key, axis)
  return axis
}

/** Real hours from the local midnight of `date` to an instant (can exceed the day's length for later days). */
export function hoursSinceLocalMidnight(iso: string, date: string, timeZone: string): number {
  return (Date.parse(iso) - dayAxis(date, timeZone).start) / HOUR
}

export interface DayWindow {
  date: string
  axis: DayAxis
  /** Whether `date` is the place's current calendar day at `now`. */
  isToday: boolean
  /** The day's hours, oldest first, one per real hour: earlier-today observations (when the report carries them) then the forecast. */
  hours: HourPoint[]
  x0: 0
  /** The day's length in real hours. */
  x1: number
  /** Real hours from midnight to `now`; null on any other day. */
  nowHour: number | null
}

/**
 * The chart window of one day. Past hours of today come from `report.earlier_today` (older
 * reports lack it: then they are simply not plotted); the forecast series wins where both
 * carry an hour. Other days are the forecast hours of that date, nothing dimmed, no "now".
 */
export function dayWindow(report: WeatherReport, date: string, now: number = Date.now()): DayWindow {
  const tz = report.location.timezone
  const axis = dayAxis(date, tz)
  const isToday = localDate(new Date(now).toISOString(), tz) === date
  const byTime = new Map<number, HourPoint>()
  for (const h of report.earlier_today ?? []) if (localDate(h.time, tz) === date) byTime.set(Date.parse(h.time), h)
  for (const h of hoursForDate(report, date)) byTime.set(Date.parse(h.time), h)
  const hours = [...byTime.entries()].sort((a, b) => a[0] - b[0]).map(([, h]) => h)
  return { date, axis, isToday, hours, x0: 0, x1: axis.length, nowHour: isToday ? (now - axis.start) / HOUR : null }
}

/** The hours to chart for a day, and the x range (0 → the day's length). */
export function chartHours(report: WeatherReport, date: string, now: number = Date.now()): { hours: HourPoint[]; x0: 0; x1: number } {
  const { hours, x0, x1 } = dayWindow(report, date, now)
  return { hours, x0, x1 }
}

/** Ticks at every `step`-th wall-clock hour (12 AM, 3 AM…), each at its true elapsed x. */
export function axisTicks(axis: DayAxis, step: number): { x: number; hour: number }[] {
  const out: { x: number; hour: number }[] = []
  for (let x = 0; x < axis.length - 1e-9; x += 0.25) {
    const f = localHour(axis.start + x * HOUR, axis.timeZone)
    const k = f / step
    if (Math.abs(k - Math.round(k)) < 1e-9) out.push({ x, hour: Math.round(f) % 24 })
  }
  return out
}

/** Whether elapsed hour `x` falls in the wall-clock hour that happens twice. */
export function inRepeat(axis: DayAxis, x: number): boolean {
  return !!axis.repeat && x >= axis.repeat.from - 1e-9 && x < axis.repeat.to - 1e-9
}

/** "EDT" / "EST": which of the two repeated hours an instant is. */
export function zoneAbbr(ms: number, timeZone: string): string {
  return formatter(timeZone, { timeZoneName: 'short' }).formatToParts(new Date(ms)).find((p) => p.type === 'timeZoneName')?.value ?? ''
}

/** The wall-clock hour (0–23) at elapsed hour `x`. */
export function clockHourAt(axis: DayAxis, x: number): number {
  return Math.floor(localHour(axis.start + x * HOUR + 1000, axis.timeZone)) % 24
}

/**
 * "9 PM", "midnight", "noon" for elapsed hour `x`, with the zone ("1 AM EST") on the two
 * repeated hours of a fall-back day so they read as different times.
 */
export function clockPhrase(axis: DayAxis, x: number, clock: 12 | 24): string {
  const h = clockHourAt(axis, x)
  const text = clock === 24 ? `${String(h).padStart(2, '0')}:00` : h === 0 ? 'midnight' : h === 12 ? 'noon' : h < 12 ? `${h} AM` : `${h - 12} PM`
  return inRepeat(axis, x) ? `${text} ${zoneAbbr(axis.start + x * HOUR, axis.timeZone)}` : text
}

/** The hour label of a point as the scrub bubble shows it ("1 AM EST" on the repeated hour). */
export function scrubHour(axis: DayAxis, x: number, iso: string, clock: 12 | 24): string {
  const text = fmtHour(iso, axis.timeZone, clock)
  return inRepeat(axis, x) ? `${text} ${zoneAbbr(Date.parse(iso), axis.timeZone)}` : text
}

export interface Extreme<P> {
  /** The plotted point: where the series really peaks (or bottoms) within the day. */
  p: P
  /** The number the marker is labelled with. */
  value: number
}

/**
 * The day's high and low markers. Each sits at the hour where the charted series peaks / bottoms
 * on this calendar day; its label is the header's number for the day when the panel has one, so the
 * chart and the figure above it can never disagree. (If the series extreme differs from the
 * header's by more than 0.5° — the header folds in observed extremes and the engine's own daily
 * consensus — the marker still sits at the series' hour but reads the header's value.) The low
 * is null when there is only one point.
 */
export function pickExtremes<P extends { v: number }>(
  pts: P[],
  header?: { high?: number | null; low?: number | null },
): { high: Extreme<P>; low: Extreme<P> | null } {
  const hi = pts.reduce((a, p) => (p.v > a.v ? p : a))
  const lo = pts.reduce((a, p) => (p.v < a.v ? p : a))
  return {
    high: { p: hi, value: header?.high ?? hi.v },
    low: lo === hi ? null : { p: lo, value: header?.low ?? lo.v },
  }
}
